import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AuthVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (hasVerified.current) return;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('Invalid login link. No token provided.');
      return;
    }

    hasVerified.current = true;

    const verify = async () => {
      const result = await verifyMagicLink(token);

      if (result.success) {
        setStatus('success');
        // Redirect after a short delay
        setTimeout(() => {
          navigate(result.redirectUrl || '/billy/start');
        }, 1500);
      } else {
        setStatus('error');
        setError(result.error);
      }
    };

    verify();
  }, [searchParams, verifyMagicLink, navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'verifying' && (
          <>
            <div style={styles.spinner} />
            <h2 style={styles.title}>Verifying Login...</h2>
            <p style={styles.message}>Please wait while we verify your login link.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={styles.successIcon}>&#x2713;</div>
            <h2 style={styles.title}>You’re in!</h2>
            <p style={styles.message}>Taking you to the monitor...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={styles.errorIcon}>&#x2717;</div>
            <h2 style={styles.title}>Login Failed</h2>
            <p style={styles.errorMessage}>{error}</p>
            <button
              onClick={() => navigate('/login')}
              style={styles.button}
            >
              Request New Login Link
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    backgroundColor: '#2a2a2a',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #3a3a3a',
    borderTopColor: '#4CAF50',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite'
  },
  successIcon: {
    fontSize: '64px',
    color: '#4CAF50',
    marginBottom: '20px'
  },
  errorIcon: {
    fontSize: '64px',
    color: '#f44336',
    marginBottom: '20px'
  },
  title: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0 0 15px 0'
  },
  message: {
    color: '#888',
    fontSize: '16px',
    lineHeight: '1.6'
  },
  errorMessage: {
    color: '#f44336',
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '30px'
  },
  button: {
    backgroundColor: '#4CAF50',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default AuthVerify;
